
import React, { useState, useEffect } from 'react';
import {
    Briefcase,
    ChevronRight,
    ShieldCheck,
    Zap,
    TrendingUp,
    Calculator,
    Info,
    X,
    Star,
    AlertTriangle,
    Users,
    Clock,
    CheckCircle2,
    Send,
    MessageSquare
} from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { useBlockDeal, BlockDeal } from '@/shared/hooks/useBlockDeal';
import { useAuth } from '@/shared/hooks/useAuth';
import { useKYC } from '@/shared/hooks/useKYC';
import { useNavigate } from 'react-router-dom';
import { db } from '@/lib/firebase';
import { collection, addDoc, serverTimestamp, query, where, onSnapshot, Timestamp } from 'firebase/firestore';
import { useTransactionManager } from '@/shared/hooks/useTransactionManager';

interface MyInquiry {
    id: string;
    dealId?: string;
    dealTitle: string;
    message: string;
    status: 'pending' | 'replied';
    adminReply?: string;
    repliedAt?: Timestamp;
    createdAt?: Timestamp;
}

const BlockDealPage: React.FC = () => {
    const { deals, loading } = useBlockDeal();
    const { user } = useAuth();
    const { status: kycStatus, loading: kycLoading } = useKYC(user?.uid);
    const navigate = useNavigate();
    const { addTransaction } = useTransactionManager();
    const [selectedDeal, setSelectedDeal] = useState<BlockDeal | null>(null);
    const [inquiryStep, setInquiryStep] = useState<'view' | 'confirm' | 'success'>('view');
    const [inquiryMessage, setInquiryMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [myInquiries, setMyInquiries] = useState<MyInquiry[]>([]);
    const [showMyInquiries, setShowMyInquiries] = useState(false);
    const [showKYCWarning, setShowKYCWarning] = useState(false);

    // 내 문의 내역 로드
    useEffect(() => {
        if (!user) return;
        const q = query(
            collection(db, 'block_deal_inquiries'),
            where('userId', '==', user.uid)
        );
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() })) as MyInquiry[];
            const sortedData = data.sort((a, b) => {
                const timeA = a.createdAt?.toMillis() || 0;
                const timeB = b.createdAt?.toMillis() || 0;
                return timeB - timeA;
            });
            setMyInquiries(sortedData);
        }, (error) => {
            console.error("Firestore Listen Error:", error);
        });
        return () => unsubscribe();
    }, [user]);

    const formatDate = (ts?: Timestamp) => {
        if (!ts) return '';
        const d = ts.toDate();
        return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    };

    const openDetail = (deal: BlockDeal) => {
        setSelectedDeal(deal);
        setInquiryStep('view');
        setInquiryMessage('');
    };

    const closeModal = () => {
        setSelectedDeal(null);
        setInquiryStep('view');
        setInquiryMessage('');
    };

    const submitInquiry = async () => {
        // Check KYC level before allowing inquiry
        if (!kycStatus || kycStatus.level < 3) {
            setShowKYCWarning(true);
            return;
        }

        if (!selectedDeal || !user) return;
        setSubmitting(true);
        try {
            // 1. 문의 내역 저장
            await addDoc(collection(db, 'block_deal_inquiries'), {
                dealId: selectedDeal.id,
                dealTitle: selectedDeal.title,
                userId: user.uid,
                userEmail: user.email || '',
                message: inquiryMessage,
                status: 'pending',
                createdAt: serverTimestamp()
            });

            // 2. 통합 트랜잭션 원장에 기록 (어드민 모니터링용)
            await addTransaction({
                userId: user.uid,
                userName: user.displayName || user.email?.split('@')[0] || 'User',
                type: 'BLOCK_DEAL',
                asset: `BLOCK DEAL 문의: ${selectedDeal.title}`,
                amount: 'INQUIRY',
                status: 'PENDING'
            });

            setInquiryStep('success');
        } catch (e: any) {
            alert('문의 등록 실패: ' + e.message);
        }
        setSubmitting(false);
    };

    if (loading || kycLoading) {
        return <div className="min-h-screen flex items-center justify-center">
            <Zap className="h-12 w-12 text-indigo-500 animate-spin" />
        </div>;
    }


    return (
        <div className="min-h-screen pb-24 text-foreground animate-in fade-in duration-1000">
            <div className="max-w-6xl mx-auto px-4 lg:px-8 space-y-12">
                {/* Header Section */}
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10 pt-8">
                    <div className="space-y-4 max-w-2xl text-left">
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-600 dark:text-indigo-500 transition-colors">
                            <Briefcase className="h-3.5 w-3.5 fill-indigo-500" />
                            <span className="text-[10px] font-black uppercase tracking-[0.2em]">기관 전용 대량 매물</span>
                        </div>
                        <h1 className="text-4xl md:text-5xl font-black tracking-tighter leading-[0.9] uppercase">
                            대량 장외거래의 시작,<br />
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 dark:from-indigo-500 dark:via-violet-500 dark:to-purple-500">
                                프리미엄 블록딜
                            </span>
                        </h1>
                        <p className="text-muted-foreground font-bold text-sm md:text-base leading-relaxed max-w-lg uppercase tracking-tight">
                            전문 투자자를 위한 대규모 자산 매입 기회.<br />
                            시장가 대비 유리한 조건으로 대량 물량을 확보하세요.
                        </p>
                    </div>

                    {/* Stats Card */}
                    <div className="p-8 rounded-[2.5rem] bg-card border border-border shadow-2xl relative overflow-hidden group min-w-[320px] transition-colors">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 blur-3xl rounded-full group-hover:bg-indigo-500/10 transition-colors"></div>
                        <div className="relative z-10 flex items-center justify-between gap-6">
                            <div className="space-y-1">
                                <span className="text-[10px] font-black text-muted-foreground uppercase tracking-widest italic">진행 중인 블록딜</span>
                                <h3 className="text-3xl font-black text-indigo-600 dark:text-indigo-500 tracking-tighter tabular-nums">{deals.filter(d => d.status === 'Active').length}건</h3>
                            </div>
                            <div className="p-4 rounded-2xl bg-indigo-500/10">
                                <Briefcase className="h-6 w-6 text-indigo-600 dark:text-indigo-500" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Features Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[
                        { icon: ShieldCheck, title: '검증된 자산', desc: '기관급 안전성 검토 완료', color: 'text-indigo-500' },
                        { icon: Calculator, title: '정산 매트릭스', desc: '실시간 자동 정산 시스템', color: 'text-purple-500' },
                        { icon: Info, title: '리스크 경감', desc: '담보 기반의 투자자 보호', color: 'text-blue-500' },
                    ].map((item, i) => (
                        <div key={i} className="group p-8 rounded-[2.5rem] bg-card border border-border hover:border-indigo-500/20 transition-all flex items-center gap-6 shadow-xl overflow-hidden relative">
                            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                            <div className={cn("w-14 h-14 rounded-2xl bg-accent border border-border flex items-center justify-center transition-transform group-hover:scale-110 duration-500", item.color)}>
                                <item.icon className="h-6 w-6" />
                            </div>
                            <div className="text-left">
                                <h4 className="text-[11px] font-black text-foreground uppercase tracking-widest leading-none">{item.title}</h4>
                                <p className="text-[10px] font-bold text-muted-foreground mt-2 uppercase tracking-tighter italic">{item.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Deals List */}
                <div className="space-y-8">
                    {deals.map((deal) => (
                        <div key={deal.id} className="group relative rounded-[3rem] bg-card border border-border p-10 hover:border-indigo-500/30 transition-all duration-500 cursor-pointer shadow-2xl overflow-hidden text-left" onClick={() => openDetail(deal)}>
                            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/[0.01] blur-3xl -mr-32 -mt-32 rounded-full group-hover:bg-indigo-500/[0.03] transition-colors duration-700"></div>

                            <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-12">
                                <div className="flex-1 space-y-6">
                                    <div className="flex items-center gap-4">
                                        <span className={cn(
                                            "px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.2em] border",
                                            deal.status === 'Active' ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" : "bg-accent text-muted-foreground border-border"
                                        )}>
                                            {deal.status === 'Active' ? '활성' : deal.status === 'SoldOut' ? '매진' : '종료'}
                                        </span>
                                        <span className="px-3 py-1 rounded-lg bg-indigo-500/10 text-indigo-500 text-[10px] font-black uppercase">{deal.asset}</span>
                                        <h3 className="text-2xl md:text-3xl font-black text-foreground group-hover:text-indigo-600 transition-colors uppercase tracking-tight">{deal.title}</h3>
                                    </div>
                                    <p className="text-sm font-bold text-muted-foreground max-w-2xl leading-relaxed uppercase tracking-tight">{deal.description}</p>
                                    <div className="flex items-center gap-12">
                                        <div className="space-y-1">
                                            <span className="text-[10px] font-black text-muted-foreground uppercase tracking-widest italic">예상 수익률</span>
                                            <p className="text-3xl font-black text-emerald-600 dark:text-emerald-500 tracking-tighter tabular-nums">{deal.roi}</p>
                                        </div>
                                        <div className="w-px h-10 bg-border"></div>
                                        <div className="space-y-1">
                                            <span className="text-[10px] font-black text-muted-foreground uppercase tracking-widest italic">계약 기간</span>
                                            <p className="text-3xl font-black text-foreground tracking-tighter uppercase leading-none">{deal.period}</p>
                                        </div>
                                        <div className="w-px h-10 bg-border"></div>
                                        <div className="space-y-1">
                                            <span className="text-[10px] font-black text-muted-foreground uppercase tracking-widest italic">최소 투자금</span>
                                            <p className="text-xl font-black text-foreground tracking-tighter leading-none">₩{(deal.minInvestment || 0).toLocaleString()}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="w-full lg:w-80 p-8 rounded-[2.5rem] bg-accent/50 border border-border space-y-6">
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest italic">
                                            <span className="text-muted-foreground italic">모집 진행률</span>
                                            <span className="text-foreground">{deal.progress}%</span>
                                        </div>
                                        <div className="h-2.5 w-full bg-accent border border-border rounded-full overflow-hidden p-0.5">
                                            <div
                                                className={cn(
                                                    "h-full rounded-full transition-all duration-1000",
                                                    deal.status === 'Active' ? "bg-gradient-to-r from-indigo-500 to-purple-500 shadow-[0_0_10px_rgba(99,102,241,0.3)]" : "bg-muted-foreground/30"
                                                )}
                                                style={{ width: `${deal.progress}%` }}
                                            />
                                        </div>
                                    </div>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); openDetail(deal); }}
                                        className={cn(
                                            "w-full h-16 rounded-2xl font-black text-[11px] uppercase tracking-[0.2em] flex items-center justify-center gap-3 transition-all shadow-xl hover:scale-[1.02] active:scale-95 group/btn",
                                            deal.status === 'Active' ? "bg-indigo-600 text-white shadow-indigo-600/20 hover:bg-indigo-500" : "bg-accent border border-border text-muted-foreground"
                                        )}
                                    >
                                        상세 정보 보기 <ChevronRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}

                    {deals.length === 0 && (
                        <div className="text-center py-20 opacity-30">
                            <p className="text-xs font-black uppercase tracking-widest">현재 이용 가능한 블록딜 상품이 없습니다.</p>
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-center gap-4 py-10 opacity-30">
                    <Zap className="h-4 w-4 text-indigo-500 animate-pulse" />
                    <p className="text-[10px] font-black uppercase tracking-[0.4em]">독자적인 프라이빗 블록딜 정산 원장</p>
                </div>
            </div>

            {myInquiries.length > 0 && (
                <div className="fixed bottom-24 right-6 z-40 animate-in slide-in-from-right-10 duration-500">
                    <button
                        onClick={() => setShowMyInquiries(true)}
                        className="group flex items-center gap-3 bg-indigo-600 hover:bg-indigo-500 text-white p-2 pl-5 rounded-full shadow-2xl shadow-indigo-600/30 transition-all hover:scale-105 active:scale-95"
                    >
                        <span className="text-[11px] font-black uppercase tracking-wider">내 문의 답변 확인</span>
                        <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center relative">
                            <MessageSquare className="h-5 w-5" />
                            {myInquiries.some(i => i.status === 'replied') && (
                                <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-emerald-500 border-2 border-indigo-600 rounded-full animate-bounce"></span>
                            )}
                        </div>
                    </button>
                </div>
            )}

            {selectedDeal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={closeModal}>
                    <div
                        className="bg-card border border-border rounded-[3rem] w-full max-w-3xl max-h-[90vh] overflow-y-auto p-10 shadow-2xl animate-in fade-in zoom-in-95 duration-300"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex items-start justify-between mb-8">
                            <div className="space-y-3">
                                <div className="flex items-center gap-3">
                                    <span className={cn(
                                        "px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.2em] border",
                                        selectedDeal.status === 'Active' ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" : "bg-accent text-muted-foreground border-border"
                                    )}>
                                        {selectedDeal.status === 'Active' ? '모집 중' : '종료'}
                                    </span>
                                    <span className="px-3 py-1 rounded-lg bg-indigo-500/10 text-indigo-500 text-[10px] font-black uppercase">{selectedDeal.asset}</span>
                                    {myInquiries.some(i => i.dealId === selectedDeal.id && i.status === 'replied') && (
                                        <span className="px-3 py-1 rounded-lg bg-emerald-500/10 text-emerald-500 text-[10px] font-black uppercase flex items-center gap-1">
                                            <CheckCircle2 className="h-3 w-3" /> 답변 완료
                                        </span>
                                    )}
                                </div>
                                <h2 className="text-2xl font-black text-foreground uppercase tracking-tight">{selectedDeal.title}</h2>
                            </div>
                            <button
                                onClick={closeModal}
                                className="w-12 h-12 rounded-2xl bg-accent border border-border flex items-center justify-center text-muted-foreground hover:text-foreground transition-all"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <p className="text-sm font-bold text-muted-foreground leading-relaxed mb-8">{selectedDeal.description}</p>

                        {myInquiries.some(i => i.dealId === selectedDeal.id) && inquiryStep === 'view' && (
                            <div className="mb-8 p-6 rounded-[2rem] bg-indigo-500/5 border border-indigo-500/10 animate-in slide-in-from-top-4 duration-500">
                                <h4 className="text-xs font-black text-indigo-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                                    <MessageSquare className="h-4 w-4" /> 내 문의 및 답변 현황
                                </h4>
                                <div className="space-y-4">
                                    {myInquiries.filter(i => i.dealId === selectedDeal.id).map(inq => (
                                        <div key={inq.id} className="space-y-3">
                                            <div className="flex justify-between items-start gap-4">
                                                <div className="space-y-1">
                                                    <p className="text-sm font-bold text-foreground leading-snug">{inq.message || '(메시지 없음)'}</p>
                                                    <p className="text-[10px] text-muted-foreground font-black">{formatDate(inq.createdAt)}</p>
                                                </div>
                                                <span className={cn(
                                                    "text-[8px] font-black px-2 py-0.5 rounded-md uppercase tracking-tighter shrink-0",
                                                    inq.status === 'pending' ? "bg-amber-500/10 text-amber-500" : "bg-emerald-500/10 text-emerald-500"
                                                )}>
                                                    {inq.status === 'pending' ? '대기' : '답변완료'}
                                                </span>
                                            </div>
                                            {inq.adminReply && (
                                                <div className="pl-4 border-l-2 border-indigo-500/30 py-1">
                                                    <p className="text-xs font-bold text-indigo-600 dark:text-indigo-400">
                                                        <span className="text-[10px] font-black uppercase opacity-60 mr-2">Admin:</span>
                                                        {inq.adminReply}
                                                    </p>
                                                    <p className="text-[9px] text-muted-foreground mt-1">{formatDate(inq.repliedAt)}</p>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                            <div className="p-5 rounded-2xl bg-accent/50 border border-border text-center space-y-2">
                                <TrendingUp className="h-5 w-5 text-emerald-500 mx-auto" />
                                <p className="text-[9px] font-black text-muted-foreground uppercase tracking-widest">예상 수익률</p>
                                <p className="text-2xl font-black text-emerald-600 dark:text-emerald-500 tabular-nums">{selectedDeal.roi}</p>
                            </div>
                            <div className="p-5 rounded-2xl bg-accent/50 border border-border text-center space-y-2">
                                <Clock className="h-5 w-5 text-blue-500 mx-auto" />
                                <p className="text-[9px] font-black text-muted-foreground uppercase tracking-widest">계약 기간</p>
                                <p className="text-2xl font-black text-foreground">{selectedDeal.period}</p>
                            </div>
                            <div className="p-5 rounded-2xl bg-accent/50 border border-border text-center space-y-2">
                                <Users className="h-5 w-5 text-indigo-500 mx-auto" />
                                <p className="text-[9px] font-black text-muted-foreground uppercase tracking-widest">투자자 수</p>
                                <p className="text-2xl font-black text-foreground tabular-nums">{selectedDeal.investorCount}명</p>
                            </div>
                            <div className="p-5 rounded-2xl bg-accent/50 border border-border text-center space-y-2">
                                <AlertTriangle className="h-5 w-5 text-amber-500 mx-auto" />
                                <p className="text-[9px] font-black text-muted-foreground uppercase tracking-widest">리스크</p>
                                <p className={cn("text-2xl font-black", selectedDeal.riskLevel === '낮음' ? 'text-emerald-500' : selectedDeal.riskLevel === '보통' ? 'text-amber-500' : 'text-rose-500')}>
                                    {selectedDeal.riskLevel}
                                </p>
                            </div>
                        </div>

                        <div className="p-6 rounded-2xl bg-accent/30 border border-border space-y-4 mb-8">
                            <h4 className="text-xs font-black text-foreground uppercase tracking-widest">투자 현황</h4>
                            <div className="grid grid-cols-3 gap-4 text-center">
                                <div>
                                    <p className="text-[9px] font-black text-muted-foreground uppercase">목표 금액</p>
                                    <p className="text-lg font-black text-foreground tabular-nums">₩{((selectedDeal.targetAmount || 0) / 100000000).toFixed(0)}억</p>
                                </div>
                                <div>
                                    <p className="text-[9px] font-black text-muted-foreground uppercase">현재 모집액</p>
                                    <p className="text-lg font-black text-indigo-500 tabular-nums">₩{((selectedDeal.currentAmount || 0) / 100000000).toFixed(1)}억</p>
                                </div>
                                <div>
                                    <p className="text-[9px] font-black text-muted-foreground uppercase">최소 투자금</p>
                                    <p className="text-lg font-black text-foreground tabular-nums">₩{(selectedDeal.minInvestment || 0).toLocaleString()}</p>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <div className="flex justify-between text-[10px] font-black uppercase tracking-widest">
                                    <span className="text-muted-foreground">모집 진행률</span>
                                    <span className="text-foreground">{selectedDeal.progress}%</span>
                                </div>
                                <div className="h-3 w-full bg-accent border border-border rounded-full overflow-hidden">
                                    <div
                                        className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-1000"
                                        style={{ width: `${selectedDeal.progress}%` }}
                                    />
                                </div>
                            </div>
                        </div>

                        {selectedDeal.features && selectedDeal.features.length > 0 && (
                            <div className="mb-8">
                                <h4 className="text-xs font-black text-foreground uppercase tracking-widest mb-4">상품 특장점</h4>
                                <div className="grid grid-cols-2 gap-3">
                                    {selectedDeal.features.map((feature, i) => (
                                        <div key={i} className="flex items-center gap-3 p-4 rounded-xl bg-accent/30 border border-border">
                                            <Star className="h-4 w-4 text-indigo-500 shrink-0" />
                                            <span className="text-xs font-bold text-foreground">{feature}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {inquiryStep === 'view' && (
                            <div className="flex gap-4">
                                <button onClick={closeModal} className="flex-1 h-16 rounded-2xl bg-accent border border-border text-muted-foreground font-black text-xs uppercase tracking-widest transition-all hover:bg-accent/80">닫기</button>
                                {selectedDeal.status === 'Active' && <button onClick={() => setInquiryStep('confirm')} className="flex-1 h-16 rounded-2xl bg-indigo-600 text-white font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-indigo-500 transition-all shadow-xl shadow-indigo-600/20">투자 문의하기</button>}
                            </div>
                        )}

                        {inquiryStep === 'confirm' && (
                            <div className="space-y-5 animate-in fade-in duration-300">
                                <div className="p-5 rounded-2xl bg-indigo-500/10 border border-indigo-500/20">
                                    <h4 className="text-xs font-black text-indigo-500 uppercase tracking-widest mb-3">투자 문의 접수</h4>
                                    <div className="grid grid-cols-2 gap-3 text-xs">
                                        <div><span className="text-muted-foreground font-bold">상품명:</span><p className="font-black text-foreground mt-0.5">{selectedDeal.title}</p></div>
                                        <div><span className="text-muted-foreground font-bold">예상 수익률:</span><p className="font-black text-emerald-500 mt-0.5">{selectedDeal.roi}</p></div>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-2 block">문의 내용 (선택사항)</label>
                                    <textarea value={inquiryMessage} onChange={e => setInquiryMessage(e.target.value)} className="w-full h-24 bg-accent border border-border rounded-xl px-4 py-3 text-sm font-bold text-foreground focus:outline-none focus:border-indigo-500/50 resize-none" placeholder="투자 금액, 기간 등 문의사항을 입력해주세요" />
                                </div>
                                <div className="flex gap-4">
                                    <button onClick={() => setInquiryStep('view')} className="flex-1 h-14 rounded-2xl bg-accent border border-border text-muted-foreground font-black text-xs uppercase tracking-widest transition-all hover:bg-accent/80">뒤로</button>
                                    <button onClick={submitInquiry} disabled={submitting} className="flex-1 h-14 rounded-2xl bg-indigo-600 text-white font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-indigo-500 transition-all shadow-xl shadow-indigo-600/20 disabled:opacity-50"><Send className="h-4 w-4" />{submitting ? '접수 중...' : '문의 접수하기'}</button>
                                </div>
                            </div>
                        )}

                        {inquiryStep === 'success' && (
                            <div className="text-center space-y-6 py-4 animate-in fade-in duration-300">
                                <div className="w-20 h-20 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto"><CheckCircle2 className="h-10 w-10 text-emerald-500" /></div>
                                <div className="space-y-2"><h4 className="text-lg font-black text-foreground uppercase">문의가 접수되었습니다</h4><p className="text-sm text-muted-foreground font-bold">담당자가 검토 후 빠른 시일 내에 연락드리겠습니다.</p></div>
                                <button onClick={closeModal} className="h-14 px-12 rounded-2xl bg-indigo-600 text-white font-black text-xs uppercase tracking-widest hover:bg-indigo-500 transition-all shadow-xl shadow-indigo-600/20">확인</button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {showMyInquiries && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setShowMyInquiries(false)}>
                    <div className="bg-card border border-border rounded-[3rem] w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8 shadow-2xl animate-in fade-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-black text-foreground uppercase tracking-tight flex items-center gap-3"><MessageSquare className="h-6 w-6 text-indigo-500" />내 문의 내역</h2>
                            <button onClick={() => setShowMyInquiries(false)} className="w-10 h-10 rounded-xl bg-accent border border-border flex items-center justify-center text-muted-foreground hover:text-foreground transition-all"><X className="h-5 w-5" /></button>
                        </div>
                        <div className="space-y-4">
                            {myInquiries.map((inq) => (
                                <div key={inq.id} className="rounded-2xl border border-border overflow-hidden">
                                    <div className="p-4 bg-accent/30 flex items-center justify-between"><div><p className="text-xs font-black text-foreground">{inq.dealTitle}</p><p className="text-[10px] text-muted-foreground font-bold mt-0.5">{formatDate(inq.createdAt)}</p></div><span className={cn("text-[9px] font-black px-3 py-1 rounded-full uppercase", inq.status === 'pending' ? "bg-amber-500/10 text-amber-500 border border-amber-500/20" : "bg-emerald-500/10 text-emerald-500 border border-emerald-500/20")}>{inq.status === 'pending' ? '대기 중' : '답변 완료'}</span></div>
                                    <div className="p-4"><p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-1">내 문의</p><p className="text-sm font-bold text-foreground">{inq.message || '(메시지 없음)'}</p></div>
                                    {inq.adminReply ? (<div className="p-4 bg-indigo-500/5 border-t border-indigo-500/10"><p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest mb-1">관리자 답변 · {formatDate(inq.repliedAt)}</p><p className="text-sm font-bold text-foreground">{inq.adminReply}</p></div>) : (<div className="p-4 border-t border-border"><p className="text-xs text-muted-foreground font-bold italic flex items-center gap-2"><Clock className="h-3 w-3" />답변 대기 중입니다</p></div>)}
                                </div>
                            ))}
                        </div>
                        <button onClick={() => setShowMyInquiries(false)} className="w-full h-14 rounded-2xl bg-accent border border-border text-muted-foreground font-black text-xs uppercase tracking-widest transition-all hover:bg-accent/80 mt-6">닫기</button>
                    </div>
                </div>
            )}

            {/* KYC Warning Modal */}
            {showKYCWarning && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
                    <div className="bg-card border border-border rounded-[3rem] w-full max-w-md p-10 space-y-8 shadow-2xl scale-in-center">
                        <div className="w-20 h-20 mx-auto rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                            <ShieldCheck className="h-10 w-10 text-amber-500" />
                        </div>
                        <div className="space-y-4 text-center">
                            <h2 className="text-2xl font-black text-foreground uppercase tracking-tight">KYC 레벨 3 인증 필요</h2>
                            <p className="text-sm font-bold text-muted-foreground leading-relaxed">
                                블록딜 서비스는 KYC 레벨 3 인증이 완료된 사용자만 이용하실 수 있습니다.<br />
                                주소 인증과 사업자 인증을 완료해주세요.
                            </p>
                            <p className="text-xs font-bold text-muted-foreground">현재 KYC 레벨: {kycStatus?.level || 0} / 3</p>
                        </div>
                        <div className="flex gap-4">
                            <button
                                onClick={() => setShowKYCWarning(false)}
                                className="flex-1 h-14 rounded-xl bg-card border border-border text-xs font-black uppercase"
                            >
                                닫기
                            </button>
                            <button
                                onClick={() => navigate('/verification')}
                                className="flex-1 h-14 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-black text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2"
                            >
                                <ShieldCheck className="h-5 w-5" />
                                인증하기
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BlockDealPage;
